import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Cable,
  CloudDownload,
  CloudUpload,
  ContentCopy,
  Delete,
  FormatAlignLeft,
  History,
  MoreHoriz,
  OpenInNew,
  Save,
  Settings,
  Sync,
  ViewColumn,
  AccountTree,
  Traffic,
} from '@mui/icons-material';
import { deviceApi, fileStoreApi, taskApi } from '../api';
import { DeviceFileInfo, FileStore, FileTreeFolderNode, TemplateInfo, displayName } from '../types';
import { ConfigSchema, ConfigValues, generateConfigJson, inspectConfigFile, mergeConfig, deviceSupportedConfigVersion, settingsNewerThanFirmware } from '../schema/schema';
import ConfigMigrateBanner from './ConfigMigrateBanner';
import SerialConsole from './SerialConsole';
import ConfigFileTree from '../files/ConfigFileTree';
import SettingsFormDialog from '../files/SettingsFormDialog';
import FileSyncDialog from '../files/FileSyncDialog';
import { collectFilePaths } from '../files/fileTree';
import { deployJsonToDevice } from '../files/deployToDevice';
import UsbFileList from './UsbFileList';
import { defaultStoredFileContent } from '../files/storedFileDefaults';
import { catalogPath, nextCatalogName } from '../files/catalogPaths';
import { webSerialSupported } from '../flash/esptool';
import { useUsbSession } from '../flash/UsbSessionContext';
import { SpiffsEntry } from '../flash/serialSession';
import { formatDateTime, useI18n } from '../i18n';
import {
  SETUP_DISPLAY_PATH,
  SETUP_FLOW_PATH,
  SETUP_SIGNALS_PATH,
  controllerDetailPath,
  setupDisplayPath,
  setupFlowPath,
  setupSignalsPath,
} from '../nav/paths';

type Kind = 'template' | 'device';

const DEFAULT_PATH = '/settings.json';

function editorKindForPath(filePath: string): 'layout' | 'flow' | 'signals' | null {
  if (filePath.includes('/layouts/') || /^\/layout\d+\.json$/i.test(filePath)) return 'layout';
  if (filePath.includes('/flows/') || /^\/flow\d+\.json$/i.test(filePath)) return 'flow';
  if (filePath.includes('/signals/') || filePath.includes('/signals/')) return 'signals';
  return null;
}

function apiError(err: any, fallback: string): string {
  return err?.response?.data?.error || err?.message || fallback;
}

function deviceLabel(device: DeviceFileInfo): string {
  return displayName({
    id: device.id,
    hostname: device.hostname,
  });
}

const FileManager: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { deviceId: routeDeviceId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const usb = useUsbSession();
  const kind = (searchParams.get('kind') === 'template' ? 'template' : 'device') as Kind;
  const selectedId = kind === 'template'
    ? (searchParams.get('id') || '')
    : (routeDeviceId || searchParams.get('id') || '');
  const path = searchParams.get('path') || DEFAULT_PATH;

  const [store, setStore] = useState<FileStore | null>(null);
  const [content, setContent] = useState('');
  const [savedContent, setSavedContent] = useState('');
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingPullId, setPendingPullId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newModel, setNewModel] = useState('');
  const [loadTemplateId, setLoadTemplateId] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateModel, setTemplateModel] = useState('');
  const [schema, setSchema] = useState<ConfigSchema | null>(null);
  const [formValues, setFormValues] = useState<ConfigValues>({});
  const [historyOpen, setHistoryOpen] = useState(false);
  const [revisions, setRevisions] = useState<{ id: string; size: number; updatedAt: number }[]>([]);
  const [usbViewPath, setUsbViewPath] = useState<string | null>(null);
  const [usbConsoleOpen, setUsbConsoleOpen] = useState(false);
  const [fileTree, setFileTree] = useState<FileTreeFolderNode | null>(null);
  const [settingsFormOpen, setSettingsFormOpen] = useState(false);
  const [sidebarTab, setSidebarTab] = useState<'server' | 'usb'>('server');
  const [editorMenuAnchor, setEditorMenuAnchor] = useState<HTMLElement | null>(null);
  const [syncOpen, setSyncOpen] = useState(false);

  const dirty = content !== savedContent;
  const selectedTemplate = store?.templates.find((t) => t.id === selectedId);
  const selectedDevice = store?.devices.find((d) => d.id === selectedId);
  const editorPath = usbViewPath || path;
  const schemaForPath = schema && editorPath === schema.path ? schema : null;
  const configInfo = schemaForPath ? inspectConfigFile(content, schemaForPath) : null;
  const deviceConfigVersion = kind === 'device' ? deviceSupportedConfigVersion(selectedDevice) : null;
  const pushBlocked = Boolean(
    kind === 'device' &&
    schemaForPath &&
    settingsNewerThanFirmware(configInfo?.version ?? null, deviceConfigVersion ?? 0)
  );
  const locked = busy || usb.busy;

  const applyContent = (raw: string) => {
    setContent(raw);
    if (schemaForPath) {
      try {
        setFormValues(mergeConfig(schemaForPath, raw));
      } catch {
        // keep previous form values if JSON is mid-edit
      }
    }
  };

  const loadStore = useCallback(async () => {
    const next = await fileStoreApi.getStore();
    setStore(next);
    return next;
  }, []);

  const loadFileTree = useCallback(async (nextKind: Kind, id: string) => {
    if (!id) {
      setFileTree(null);
      return;
    }
    try {
      const response = nextKind === 'template'
        ? await fileStoreApi.getTemplateFileTree(id)
        : await deviceApi.getStoredFileTree(id);
      setFileTree(response.tree);
    } catch (err: any) {
      setFileTree(null);
      setError(apiError(err, t('files.treeFailed')));
    }
  }, [t]);

  const select = (nextKind: Kind, id: string, nextPath = path) => {
    if (dirty && !window.confirm(t('files.discardConfirm'))) {
      return;
    }
    if (routeDeviceId && nextKind === 'device' && id !== routeDeviceId) {
      return;
    }
    setUsbViewPath(null);
    setSearchParams({ kind: nextKind, id, path: nextPath });
  };

  const loadContent = useCallback(async (nextKind: Kind, id: string, nextPath: string, quiet = false) => {
    if (!id) {
      setContent('');
      setSavedContent('');
      setUpdatedAt(null);
      return;
    }
    try {
      setError(null);
      const stored = nextKind === 'template'
        ? await fileStoreApi.getTemplateFile(id, nextPath)
        : await deviceApi.getStoredFile(id, nextPath);
      if (stored) {
        setContent(stored.content);
        setSavedContent(stored.content);
        setUpdatedAt(stored.updatedAt);
        if (!quiet) {
          setStatus(t('files.loadedFromServer', { path: nextPath }));
        }
      } else {
        setContent('');
        setSavedContent('');
        setUpdatedAt(null);
        if (!quiet) {
          setStatus(
            nextKind === 'template'
              ? t('files.noTemplateFile', { path: nextPath })
              : t('files.noServerCopy', { path: nextPath })
          );
        }
      }
    } catch (err: any) {
      setError(apiError(err, t('files.loadFailed')));
    }
  }, []);

  useEffect(() => {
    loadStore().catch((err) => setError(apiError(err, t('files.storeFailed'))));
    fileStoreApi.getSchema().then(setSchema).catch((err) => setError(apiError(err, t('files.schemaFailed'))));
  }, [loadStore]);

  useEffect(() => {
    if (!schema || path !== schema.path) {
      return;
    }
    try {
      setFormValues(mergeConfig(schema, content));
    } catch {
      // ignore invalid JSON while editing
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, selectedId, path, savedContent]);

  useEffect(() => {
    if (selectedTemplate) {
      setTemplateName(selectedTemplate.name);
      setTemplateModel(selectedTemplate.model || '');
    }
  }, [selectedTemplate]);

  useEffect(() => {
    if (!selectedId) {
      setFileTree(null);
      return;
    }
    loadFileTree(kind, selectedId);
  }, [kind, selectedId, loadFileTree]);

  useEffect(() => {
    if (!store || selectedId) {
      return;
    }
    if (routeDeviceId) {
      setSearchParams({ kind: 'device', id: routeDeviceId, path: DEFAULT_PATH }, { replace: true });
      return;
    }
    const needsConfig = store.devices.find((d) => d.missing.includes(DEFAULT_PATH));
    const firstDevice = needsConfig || store.devices[0];
    const firstTemplate = store.templates[0];
    if (firstDevice) {
      setSearchParams({ kind: 'device', id: firstDevice.id, path: DEFAULT_PATH }, { replace: true });
    } else if (firstTemplate) {
      setSearchParams({ kind: 'template', id: firstTemplate.id, path: DEFAULT_PATH }, { replace: true });
    }
  }, [store, selectedId, routeDeviceId, setSearchParams]);

  useEffect(() => {
    if (!selectedId) {
      return;
    }
    loadContent(kind, selectedId, path);
    setPendingPullId(null);
    setUsbViewPath(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, selectedId, path]);

  useEffect(() => {
    if (!pendingPullId || kind !== 'device' || !selectedId) {
      return;
    }
    const timer = setInterval(async () => {
      try {
        const tasks = await taskApi.getTasks(selectedId);
        const task = tasks.find((t) => t.id === pendingPullId);
        if (!task || !['completed', 'failed', 'cancelled'].includes(task.state)) {
          return;
        }
        setPendingPullId(null);
        if (task.state !== 'completed') {
          setError(typeof task.result === 'string' ? task.result : t('files.pullFailed'));
          return;
        }
        await loadStore();
        await loadContent(kind, selectedId, path, true);
        setStatus(t('files.pulled', { path }));
      } catch (err: any) {
        setError(apiError(err, t('files.refreshAfterPullFailed')));
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [pendingPullId, kind, selectedId, path, loadContent, loadStore]);

  const suggestedTemplates = useMemo(() => {
    const templates = store?.templates || [];
    if (!selectedDevice) {
      return templates;
    }
    const model = selectedDevice.model.toLowerCase();
    return [...templates].sort((a, b) => {
      const aMatch = model && a.model && model.includes(a.model.toLowerCase()) ? 1 : 0;
      const bMatch = model && b.model && model.includes(b.model.toLowerCase()) ? 1 : 0;
      return bMatch - aMatch;
    });
  }, [store, selectedDevice]);

  const handleSave = async () => {
    if (!selectedId) return;
    try {
      setBusy(true);
      setError(null);
      if (kind === 'template') {
        await fileStoreApi.saveTemplateFile(selectedId, path, content);
      } else {
        await deviceApi.saveStoredFile(selectedId, path, content);
      }
      setSavedContent(content);
      setUpdatedAt(Date.now());
      await loadStore();
      setStatus(t('files.saved', { path }));
    } catch (err: any) {
      setError(apiError(err, t('files.saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  const handlePull = async () => {
    if (kind !== 'device' || !selectedId) return;
    try {
      setBusy(true);
      setError(null);
      const task = await deviceApi.pullStoredFile(selectedId, path);
      setPendingPullId(task.id);
      setStatus(t('files.queuedPull', { path }));
    } catch (err: any) {
      setError(apiError(err, t('files.pullQueueFailed')));
    } finally {
      setBusy(false);
    }
  };

  const handlePushUsb = async () => {
    if (!content) return;
    if (!webSerialSupported()) {
      setError(t('common.webSerialRequired'));
      return;
    }
    const target = editorPath.startsWith('/') ? editorPath : `/${editorPath}`;
    if (target.endsWith('.json')) {
      try {
        JSON.parse(content);
      } catch {
        setError(t('files.invalidJsonFile'));
        return;
      }
    }
    setBusy(true);
    setError(null);
    setUsbConsoleOpen(true);
    try {
      if (kind === 'device' && selectedId && target === path) {
        await deviceApi.saveStoredFile(selectedId, path, content);
        setSavedContent(content);
        setUpdatedAt(Date.now());
        await loadStore();
      }
      await usb.connect();
      if (target === '/settings.json') {
        await usb.provisionSettings(content);
      } else {
        await usb.writeFile(target, content);
        await usb.refreshFiles();
      }
      setStatus(kind === 'device' ? t('files.wroteUsbAndSaved', { path: target }) : t('files.wroteUsb', { path: target }));
    } catch (err: any) {
      setError(apiError(err, t('files.usbWriteFailed')));
    } finally {
      setBusy(false);
    }
  };

  const handleOpenUsbFile = async (file: SpiffsEntry) => {
    setUsbConsoleOpen(true);
    try {
      const body = await usb.readFile(file.path);
      setUsbViewPath(file.path);
      setContent(body);
      if (schema && file.path === schema.path) {
        setFormValues(mergeConfig(schema, body));
      }
      setStatus(t('files.loadedUsb', { path: file.path }));
    } catch (err: any) {
      setError(apiError(err, t('files.usbReadFailed')));
    }
  };

  const handleReadUsb = async () => {
    setUsbConsoleOpen(true);
    try {
      await usb.connect();
    } catch {
      return;
    }
    await handleOpenUsbFile({ name: editorPath.replace(/^\//, ''), path: editorPath, size: 0, isDir: false });
  };

  const handlePush = async () => {
    if (kind !== 'device' || !selectedId) return;
    if (path === '/settings.json' && !window.confirm(t('files.settingsPushConfirm'))) {
      return;
    }
    try {
      setBusy(true);
      setError(null);
      const result = await deployJsonToDevice(selectedId, path, content, {
        syncCatalog: path !== '/settings.json',
      });
      setSavedContent(content);
      setUpdatedAt(Date.now());
      await loadStore();
      await loadFileTree(kind, selectedId);
      const parts = [t('files.queuedPush', { id: result.taskId })];
      if (result.catalogSynced) parts.push(t('deploy.catalogSynced'));
      if (result.warnings?.length) parts.push(result.warnings.join(' '));
      setStatus(parts.join(' · '));
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.code === 'config_version_newer_than_firmware') {
        setError(t('files.pushBlockedTooNew', { file: data.fileVersion, device: data.deviceVersion }));
      } else {
        setError(apiError(err, t('files.pushFailed')));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleCreateTemplate = async (fromDevice: boolean) => {
    try {
      setBusy(true);
      setError(null);
      const template = await fileStoreApi.createTemplate({
        name: newName.trim() || t('files.defaultTemplateName'),
        model: newModel.trim(),
        path: fromDevice ? path : undefined,
        content: fromDevice ? content : undefined,
      });
      setNewOpen(false);
      setSaveAsOpen(false);
      setNewName('');
      setNewModel('');
      await loadStore();
      setSearchParams({ kind: 'template', id: template.id, path });
      setStatus(t('files.createdTemplate', { name: template.name }));
    } catch (err: any) {
      setError(apiError(err, t('files.createFailed')));
    } finally {
      setBusy(false);
    }
  };

  const handleLoadTemplate = async () => {
    if (kind !== 'device' || !selectedId || !loadTemplateId) return;
    if (content && !window.confirm(t('files.overwriteConfirm'))) {
      return;
    }
    try {
      setBusy(true);
      setError(null);
      const file = await deviceApi.copyFromTemplate(selectedId, loadTemplateId, path);
      setContent(file.content);
      setSavedContent(file.content);
      setUpdatedAt(file.updatedAt);
      setLoadOpen(false);
      await loadStore();
      setStatus(t('files.copiedTemplate', { path }));
    } catch (err: any) {
      setError(apiError(err, t('files.copyFailed')));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (kind !== 'template' || !selectedId) return;
    if (!window.confirm(t('files.deleteConfirm'))) return;
    try {
      setBusy(true);
      const templates = await fileStoreApi.deleteTemplate(selectedId);
      await loadStore();
      const next = templates[0];
      if (next) {
        setSearchParams({ kind: 'template', id: next.id, path });
      } else {
        setSearchParams({});
      }
      setStatus(t('files.deletedTemplate'));
    } catch (err: any) {
      setError(apiError(err, t('files.deleteFailed')));
    } finally {
      setBusy(false);
    }
  };

  const handlePretty = () => {
    try {
      setContent(JSON.stringify(JSON.parse(content), null, 2));
      setError(null);
    } catch {
      setError(t('files.invalidJson'));
    }
  };

  const handleFormChange = (next: ConfigValues) => {
    if (!schemaForPath) {
      return;
    }
    setFormValues(next);
    setContent(generateConfigJson(schemaForPath, next, content));
  };

  const handleConfigMigrated = (next: string) => {
    if (!schemaForPath) {
      return;
    }
    setContent(next);
    setFormValues(mergeConfig(schemaForPath, next));
    setStatus(t('files.migrated', { version: schemaForPath.configVersion }));
  };

  const openHistory = async () => {
    if (!selectedId) return;
    try {
      const list = await fileStoreApi.getRevisions(kind, selectedId, path);
      setRevisions(list);
      setHistoryOpen(true);
    } catch (err: any) {
      setError(apiError(err, t('files.historyFailed')));
    }
  };

  const restoreRevision = async (revId: string) => {
    if (!selectedId) return;
    try {
      const raw = await fileStoreApi.getRevision(kind, selectedId, path, revId);
      applyContent(raw);
      setHistoryOpen(false);
      setStatus(t('files.restored', { id: revId }));
    } catch (err: any) {
      setError(apiError(err, t('files.restoreFailed')));
    }
  };

  const openLoadDialog = () => {
    const first = suggestedTemplates[0]?.id || '';
    setLoadTemplateId(first);
    setLoadOpen(true);
  };

  const storedFiles = kind === 'device' ? selectedDevice?.files : selectedTemplate?.files;
  const storedMissing = kind === 'device' ? selectedDevice?.missing : [];
  const treePaths = fileTree ? collectFilePaths(fileTree) : [
    ...(storedFiles || []).map((f) => f.path),
    ...(storedMissing || []),
  ];

  const refreshExplorer = async () => {
    await loadStore();
    if (selectedId) {
      await loadFileTree(kind, selectedId);
    }
  };

  const handleCreateStoredFile = async (prefix: 'layout' | 'flow') => {
    if (!selectedId) {
      return;
    }
    const folder = prefix === 'layout' ? 'layouts' : 'flows';
    const nextPath = catalogPath(folder, nextCatalogName(folder, treePaths));
    const body = defaultStoredFileContent(nextPath);
    try {
      setBusy(true);
      setError(null);
      if (kind === 'device') {
        await deviceApi.saveStoredFile(selectedId, nextPath, body);
      } else {
        await fileStoreApi.saveTemplateFile(selectedId, nextPath, body);
      }
      await refreshExplorer();
      select(kind, selectedId, nextPath);
      setStatus(t('files.createdFile', { path: nextPath }));
    } catch (err: any) {
      setError(apiError(err, t('files.createFileFailed')));
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadFile = async (filePath: string) => {
    if (!selectedId) return;
    try {
      const stored = kind === 'device'
        ? await deviceApi.getStoredFile(selectedId, filePath)
        : await fileStoreApi.getTemplateFile(selectedId, filePath);
      const text = stored?.content ?? content;
      const blob = new Blob([text], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filePath.replace(/^\//, '') || 'file.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(apiError(err, t('files.downloadFailed')));
    }
  };

  const handleRenameFile = async (from: string, to: string) => {
    if (!selectedId) return;
    try {
      setBusy(true);
      if (kind === 'device') {
        await deviceApi.renameStoredFile(selectedId, from, to);
      } else {
        await fileStoreApi.renameTemplateFile(selectedId, from, to);
      }
      await refreshExplorer();
      select(kind, selectedId, to);
      setStatus(t('files.renamed', { from, to }));
    } catch (err: any) {
      setError(apiError(err, t('files.renameFileFailed')));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteFile = async (filePath: string) => {
    if (!selectedId) return;
    try {
      setBusy(true);
      if (kind === 'device') {
        await deviceApi.deleteStoredFile(selectedId, filePath);
      } else {
        await fileStoreApi.deleteTemplateFile(selectedId, filePath);
      }
      await refreshExplorer();
      if (path === filePath) {
        select(kind, selectedId, '/settings.json');
      }
      setStatus(t('files.deleted', { path: filePath }));
    } catch (err: any) {
      setError(apiError(err, t('files.deleteFailed')));
    } finally {
      setBusy(false);
    }
  };

  const handleUploadFile = async (file: File) => {
    if (!selectedId) return;
    try {
      setBusy(true);
      const text = await file.text();
      const safe = file.name.replace(/[^A-Za-z0-9._-]/g, '_');
      const nextPath = `/${safe}`;
      if (kind === 'device') {
        await deviceApi.saveStoredFile(selectedId, nextPath, text);
      } else {
        await fileStoreApi.saveTemplateFile(selectedId, nextPath, text);
      }
      await refreshExplorer();
      select(kind, selectedId, nextPath);
      setStatus(t('files.uploaded', { path: nextPath }));
    } catch (err: any) {
      setError(apiError(err, t('files.uploadFailed')));
    } finally {
      setBusy(false);
    }
  };

  const devices = [...(store?.devices || [])].sort((a, b) => {
    const missing = Number(b.missing.length > 0) - Number(a.missing.length > 0);
    if (missing) return missing;
    return deviceLabel(a).localeCompare(deviceLabel(b));
  });

  return (
    <Container maxWidth={false} sx={{ py: 2, px: { xs: 2, md: 3 }, overflow: 'auto', height: '100%' }}>
      {(error || usb.error) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setError(null); usb.clearError(); }}>
          {error || usb.error}
        </Alert>
      )}
      {status && !error && !usb.error && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              overflow: 'hidden',
              minWidth: 0,
              py: 0.25,
            },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>{status}</Box>
          {(locked || pendingPullId) && (
            <CircularProgress size={16} sx={{ flexShrink: 0 }} />
          )}
        </Alert>
      )}

      <Box display="flex" gap={2} alignItems="stretch" sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
        <Paper sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0, p: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" px={1} pt={1}>
            <Typography variant="subtitle2">{t('files.templates')}</Typography>
            <Tooltip title={t("files.newTemplate")}>
              <IconButton
                size="small"
                onClick={() => {
                  setNewName('');
                  setNewModel(selectedDevice?.model || '');
                  setNewOpen(true);
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <List dense>
            {(store?.templates || []).map((template: TemplateInfo) => (
              <ListItemButton
                key={template.id}
                selected={kind === 'template' && selectedId === template.id}
                onClick={() => select('template', template.id)}
              >
                <ListItemText
                  primary={template.name}
                  secondary={template.model || template.id}
                  primaryTypographyProps={{ noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
          <Divider />
          {routeDeviceId ? (
            <>
              <Typography variant="subtitle2" sx={{ px: 1, pt: 1 }}>
                {t('files.thisController')}
              </Typography>
              <List dense>
                <ListItemButton
                  selected={kind === 'device' && selectedId === routeDeviceId}
                  onClick={() => select('device', routeDeviceId)}
                >
                  <ListItemText
                    primary={deviceLabel(selectedDevice || { id: routeDeviceId, name: '', hostname: '', model: '', lastSeen: 0, files: [], missing: [] })}
                    secondary={selectedDevice?.model || routeDeviceId}
                    primaryTypographyProps={{ noWrap: true }}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              </List>
            </>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ px: 1, pt: 1 }}>
                {t('files.devices')}
              </Typography>
              <List dense>
                {devices.map((device) => {
                  const missing = device.missing.includes(path);
                  return (
                    <ListItemButton
                      key={device.id}
                      selected={kind === 'device' && selectedId === device.id}
                      onClick={() => select('device', device.id)}
                    >
                      <ListItemText
                        primary={deviceLabel(device)}
                        secondary={device.model || device.id}
                        primaryTypographyProps={{ noWrap: true }}
                        secondaryTypographyProps={{ noWrap: true }}
                      />
                      {missing && <Chip size="small" color="warning" label={t("common.new")} />}
                    </ListItemButton>
                  );
                })}
              </List>
            </>
          )}
          {selectedId && (
            <>
              <Tabs
                value={sidebarTab}
                onChange={(_, v) => setSidebarTab(v)}
                variant="fullWidth"
                sx={{ minHeight: 36, mb: 0.5, px: 0.5 }}
              >
                <Tab value="server" label={t('files.tabServer')} sx={{ minHeight: 36, py: 0.5 }} />
                <Tab value="usb" label={t('files.tabUsb')} sx={{ minHeight: 36, py: 0.5 }} />
              </Tabs>
              {sidebarTab === 'server' ? (
                <ConfigFileTree
                  tree={fileTree}
                  selectedPath={path}
                  disabled={locked}
                  onSelect={(nextPath) => select(kind, selectedId, nextPath)}
                  onCreate={handleCreateStoredFile}
                  onDownload={handleDownloadFile}
                  onRename={handleRenameFile}
                  onDelete={handleDeleteFile}
                  onUpload={handleUploadFile}
                />
              ) : (
                <Box px={1} pb={1}>
                  <UsbFileList selectedPath={editorPath} onOpen={handleOpenUsbFile} disabled={locked} />
                  <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap" useFlexGap>
                    <Tooltip title={webSerialSupported() ? t('files.readUsbHint') : t('common.webSerialHttps')}>
                      <span>
                        <Button size="small" variant="outlined" startIcon={<CloudDownload />} onClick={handleReadUsb} disabled={locked || !webSerialSupported()}>
                          {t('files.readUsb')}
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title={webSerialSupported() ? t('files.writeUsbHint') : t('common.webSerialHttps')}>
                      <span>
                        <Button size="small" variant="outlined" startIcon={<Cable />} onClick={handlePushUsb} disabled={locked || !content || !webSerialSupported()}>
                          {t('files.writeUsb')}
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Paper>

        <Paper sx={{ flexGrow: 1, p: 2 }}>
          {!selectedId ? (
            <Typography color="text.secondary">{t('files.selectHint')}</Typography>
          ) : (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap" mb={2}>
                <Box>
                  <Typography variant="h6">
                    {kind === 'template' ? selectedTemplate?.name || selectedId : deviceLabel(selectedDevice || { id: selectedId, name: '', hostname: '', model: '', lastSeen: 0, files: [], missing: [] })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {editorPath}
                    {usbViewPath && usbViewPath !== path ? ` · ${t('files.fromUsb')}` : ''}
                    {updatedAt && !usbViewPath ? ` · ${t('files.serverCopy', { time: formatDateTime(updatedAt) })}` : ''}
                    {dirty ? ` · ${t('common.unsaved')}` : ''}
                    {configInfo?.valid ? ` · ${t('files.configVersion', { version: configInfo.version })}` : ''}
                    {kind === 'device' && deviceConfigVersion != null ? ` · ${t('files.deviceConfigVersion', { version: deviceConfigVersion })}` : ''}
                  </Typography>
                </Box>
                {kind === 'device' && selectedId && (
                  <Button
                    size="small"
                    startIcon={<OpenInNew />}
                    onClick={() => navigate(controllerDetailPath(selectedId))}
                  >
                    {t('common.device')}
                  </Button>
                )}
              </Box>

              {kind === 'template' && selectedTemplate && (
                <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                  <TextField
                    size="small"
                    label={t("files.templateName")}
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    onBlur={async () => {
                      if (!templateName.trim() || templateName.trim() === selectedTemplate.name) {
                        return;
                      }
                      try {
                        const updated = await fileStoreApi.updateTemplate(selectedId, { name: templateName.trim() });
                        setStore((prev) => prev ? {
                          ...prev,
                          templates: prev.templates.map((t) => t.id === selectedId ? { ...t, ...updated } : t),
                        } : prev);
                      } catch (err: any) {
                        setError(apiError(err, t('files.renameFailed')));
                      }
                    }}
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    size="small"
                    label={t("files.modelHint")}
                    placeholder={t("files.modelHintPlaceholder")}
                    value={templateModel}
                    onChange={(e) => setTemplateModel(e.target.value)}
                    onBlur={async () => {
                      if (templateModel === (selectedTemplate.model || '')) {
                        return;
                      }
                      try {
                        const updated = await fileStoreApi.updateTemplate(selectedId, { model: templateModel.trim() });
                        setStore((prev) => prev ? {
                          ...prev,
                          templates: prev.templates.map((t) => t.id === selectedId ? { ...t, ...updated } : t),
                        } : prev);
                      } catch (err: any) {
                        setError(apiError(err, t('files.updateFailed')));
                      }
                    }}
                    sx={{ minWidth: 160 }}
                  />
                </Box>
              )}

              <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap alignItems="center">
                <Typography variant="overline" color="text.secondary" sx={{ width: '100%', lineHeight: 1.2 }}>
                  {t('files.groupEditor')}
                </Typography>
                <Button variant="outlined" startIcon={<Save />} onClick={handleSave} disabled={locked || !dirty}>
                  {t('files.saveOnServer')}
                </Button>
                {schemaForPath && editorPath === '/settings.json' && (
                  <Button
                    variant="outlined"
                    startIcon={<Settings />}
                    onClick={() => {
                      try {
                        setFormValues(mergeConfig(schemaForPath, content));
                        setSettingsFormOpen(true);
                      } catch {
                        setError(t('files.invalidJsonStay'));
                      }
                    }}
                    disabled={locked}
                  >
                    {t('files.editSettingsForm')}
                  </Button>
                )}
                <Button variant="text" startIcon={<FormatAlignLeft />} onClick={handlePretty} disabled={locked || !content}>
                  {t('common.formatJson')}
                </Button>
                <Button variant="text" startIcon={<History />} onClick={openHistory} disabled={locked || !selectedId}>
                  {t('common.history')}
                </Button>
                <Button
                  variant="text"
                  startIcon={<MoreHoriz />}
                  onClick={(e) => setEditorMenuAnchor(e.currentTarget)}
                  disabled={locked}
                >
                  {t('files.openInEditor')}
                </Button>
                <Menu anchorEl={editorMenuAnchor} open={Boolean(editorMenuAnchor)} onClose={() => setEditorMenuAnchor(null)}>
                  <MenuItem
                    onClick={() => {
                      setEditorMenuAnchor(null);
                      navigate(kind === 'device' && selectedId ? setupDisplayPath(selectedId) : SETUP_DISPLAY_PATH);
                    }}
                  >
                    <ViewColumn fontSize="small" sx={{ mr: 1 }} /> {t('nav.layout')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setEditorMenuAnchor(null);
                      navigate(kind === 'device' && selectedId ? setupFlowPath(selectedId) : SETUP_FLOW_PATH);
                    }}
                  >
                    <AccountTree fontSize="small" sx={{ mr: 1 }} /> {t('nav.flow')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setEditorMenuAnchor(null);
                      navigate(kind === 'device' && selectedId ? setupSignalsPath(selectedId) : SETUP_SIGNALS_PATH);
                    }}
                  >
                    <Traffic fontSize="small" sx={{ mr: 1 }} /> {t('nav.signals')}
                  </MenuItem>
                  {editorKindForPath(editorPath) === 'layout' && (
                    <MenuItem
                      onClick={() => {
                        setEditorMenuAnchor(null);
                        navigate(setupDisplayPath(kind === 'device' ? selectedId : undefined, editorPath));
                      }}
                    >
                      <OpenInNew fontSize="small" sx={{ mr: 1 }} /> {t('files.kindLayout')}
                    </MenuItem>
                  )}
                  {editorKindForPath(editorPath) === 'flow' && (
                    <MenuItem
                      onClick={() => {
                        setEditorMenuAnchor(null);
                        navigate(kind === 'device' && selectedId
                          ? `${setupFlowPath(selectedId)}&path=${encodeURIComponent(editorPath)}`
                          : `${SETUP_FLOW_PATH}?path=${encodeURIComponent(editorPath)}`);
                      }}
                    >
                      <OpenInNew fontSize="small" sx={{ mr: 1 }} /> {t('files.kindFlow')}
                    </MenuItem>
                  )}
                  {editorKindForPath(editorPath) === 'signals' && (
                    <MenuItem
                      onClick={() => {
                        setEditorMenuAnchor(null);
                        navigate(kind === 'device' && selectedId
                          ? `${setupSignalsPath(selectedId)}&path=${encodeURIComponent(editorPath)}`
                          : `${SETUP_SIGNALS_PATH}?path=${encodeURIComponent(editorPath)}`);
                      }}
                    >
                      <OpenInNew fontSize="small" sx={{ mr: 1 }} /> {t('files.kindSignals')}
                    </MenuItem>
                  )}
                </Menu>

                {kind === 'device' && (
                  <>
                    <Divider flexItem orientation="vertical" sx={{ mx: 0.5, display: { xs: 'none', sm: 'block' } }} />
                    <Typography variant="overline" color="text.secondary" sx={{ width: { xs: '100%', sm: 'auto' }, lineHeight: 1.2 }}>
                      {t('files.groupDevice')}
                    </Typography>
                    <Button variant="outlined" startIcon={<ContentCopy />} onClick={openLoadDialog} disabled={locked}>
                      {t('files.loadFromTemplate')}
                    </Button>
                    <Button variant="outlined" startIcon={<CloudDownload />} onClick={handlePull} disabled={locked}>
                      {t('files.pullFromDevice')}
                    </Button>
                    <Button variant="contained" startIcon={<CloudUpload />} onClick={handlePush} disabled={locked || !content || pushBlocked}>
                      {t('files.pushToDevice')}
                    </Button>
                    <Button variant="outlined" startIcon={<Sync />} onClick={() => setSyncOpen(true)} disabled={locked}>
                      {t('files.syncAll')}
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => {
                        setNewName(t('files.createFromDeviceName', { name: deviceLabel(selectedDevice || { id: selectedId, name: '', hostname: '', model: '', lastSeen: 0, files: [], missing: [] }) }));
                        setNewModel(selectedDevice?.model || '');
                        setSaveAsOpen(true);
                      }}
                      disabled={busy || !content}
                    >
                      {t('files.saveAsTemplate')}
                    </Button>
                  </>
                )}
                {kind === 'template' && (
                  <Button color="error" startIcon={<Delete />} onClick={handleDeleteTemplate} disabled={busy}>
                    {t('files.deleteTemplate')}
                  </Button>
                )}
              </Stack>

              {kind === 'device' && selectedDevice?.missing.includes(path) && !content && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('files.missingCopy')}
                </Alert>
              )}

              {kind === 'device' && path === '/settings.json' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('files.settingsPushWarning')}
                </Alert>
              )}

              {pushBlocked && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {t('files.pushBlockedTooNew', {
                    file: configInfo?.version,
                    device: deviceConfigVersion,
                  })}
                </Alert>
              )}

              {schemaForPath && editorPath === '/settings.json' && (
                <ConfigMigrateBanner
                  schema={schemaForPath}
                  raw={content}
                  disabled={locked}
                  onMigrated={handleConfigMigrated}
                />
              )}

              <TextField
                label={editorPath}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                multiline
                minRows={18}
                maxRows={36}
                fullWidth
                InputProps={{
                  sx: { fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 13 },
                }}
              />
            </>
          )}
        </Paper>
      </Box>

      {schemaForPath && (
        <SettingsFormDialog
          open={settingsFormOpen}
          schema={schemaForPath}
          values={formValues}
          raw={content}
          disabled={locked}
          pushWarning={kind === 'device' && path === '/settings.json'}
          onClose={() => setSettingsFormOpen(false)}
          onChange={handleFormChange}
          onMigrated={(next) => {
            handleConfigMigrated(next);
            setSettingsFormOpen(false);
          }}
        />
      )}

      <Dialog
        open={usbConsoleOpen}
        onClose={() => !busy && !usb.busy && setUsbConsoleOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t('usb.title')}</DialogTitle>
        <DialogContent dividers>
          <SerialConsole embedded locked={busy} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsbConsoleOpen(false)} disabled={busy || usb.busy}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={newOpen} onClose={() => setNewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('files.newTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("common.name")}
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("files.modelHint")}
            fullWidth
            placeholder={t("files.modelHintPlaceholderLong")}
            value={newModel}
            onChange={(e) => setNewModel(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={() => handleCreateTemplate(false)} disabled={busy}>
            {t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={saveAsOpen} onClose={() => setSaveAsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('files.saveAsTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("common.name")}
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("files.modelHint")}
            fullWidth
            value={newModel}
            onChange={(e) => setNewModel(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveAsOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={() => handleCreateTemplate(true)} disabled={busy}>
            {t('files.saveAsTemplate')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={loadOpen} onClose={() => setLoadOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('files.loadTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('files.loadHint')}
          </Typography>
          <TextField
            select
            fullWidth
            label={t("common.template")}
            value={loadTemplateId}
            onChange={(e) => setLoadTemplateId(e.target.value)}
          >
            {suggestedTemplates.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.name}{template.model ? ` (${template.model})` : ''}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleLoadTemplate} disabled={busy || !loadTemplateId} variant="contained">
            {t('files.copyToDevice')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('files.revisions')}</DialogTitle>
        <DialogContent>
          {revisions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('files.noRevisions')}
            </Typography>
          ) : (
            <List dense>
              {revisions.map((rev) => (
                <ListItemButton key={rev.id} onClick={() => restoreRevision(rev.id)}>
                  <ListItemText
                    primary={formatDateTime(rev.updatedAt)}
                    secondary={t('files.revisionMeta', { id: rev.id, size: rev.size })}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {kind === 'device' && selectedId && (
        <FileSyncDialog
          open={syncOpen}
          deviceId={selectedId}
          onClose={() => setSyncOpen(false)}
          onQueued={(message) => setStatus(message)}
        />
      )}
    </Container>
  );
};

export default FileManager;

